import { useState } from "react";
import { Link } from "react-router-dom";

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const slides = [
    {
      step: "01",
      title: "Create Your Card",
      description:
        "Sign up and fill in your critical health information — allergies, medications, blood type, emergency contacts, and more. Your data is encrypted and HIPAA-compliant from day one.",
    },
    {
      step: "02",
      title: "Get Your Card",
      description:
        "Receive your durable, waterproof SAL Mine ID card with a unique QR code linked directly to your medical profile. Carry it in your wallet, wear it, or attach it to your bag.",
    },
    {
      step: "03",
      title: "Stay Protected",
      description:
        "In any emergency, first responders scan your QR code and instantly access your vital health information — no app needed, no passwords, no delays. Just fast, accurate care.",
    },
  ];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleContactChange = (e) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>

      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-3 focus:outline-none"
          >
            <img src={`${process.env.PUBLIC_URL}/SAL-logoR3.jpg`} alt="SAL Mine Logo" className="h-10 w-10 object-contain rounded" />
            <div className="flex flex-col items-start">
              <span className="text-xl font-extrabold text-[#2d348f] tracking-tight leading-tight">SAL Mine</span>
              <span className="text-[10px] text-[#837f90] font-medium leading-tight">
                Patent No. US 10,940,711 B2 &nbsp;·&nbsp; Patented in US, Europe &amp; China
              </span>
            </div>
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {[
              { label: "Features", id: "features" },
              { label: "Specifications", id: "specifications" },
              { label: "Applications", id: "applications" },
              { label: "Testimonials", id: "testimonials" },
              { label: "Contact", id: "contact" },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="text-sm font-medium text-gray-700 hover:text-[#2d348f] transition-colors focus:outline-none"
              >
                {item.label}
              </button>
            ))}
            <Link
              to="/login"
              className="ml-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#445eed] to-[#2d348f] text-white font-bold text-sm hover:from-[#3916ab] hover:to-[#4962d8] transition-all shadow-md"
            >
              Login
            </Link>
          </nav>

          <Link
            to="/login"
            className="md:hidden px-4 py-2 rounded-lg bg-gradient-to-r from-[#445eed] to-[#2d348f] text-white font-bold text-sm shadow-md"
          >
            Login
          </Link>
        </div>
      </header>

      {/* BANNER 1 */}
      <div className="w-full bg-[#445eed] py-4 px-6 text-center">
        <p className="text-white font-semibold text-base tracking-wide">
          Whose life will S.A.L save today?&nbsp;&nbsp;·&nbsp;&nbsp;It could be yours.
        </p>
      </div>

      {/* HERO */}
      <section className="relative w-full min-h-[600px] flex items-center overflow-hidden">
        <img
          src={`${process.env.PUBLIC_URL}/opt-stretcher.jpg`}
          alt="Emergency Medical"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1f5e]/90 via-[#2d348f]/70 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full flex items-center justify-between gap-12">
          <div className="flex-1 max-w-xl">
            <h1 className="text-5xl font-extrabold text-white leading-tight mb-5 drop-shadow-lg">
              Save A Life Mine
            </h1>
            <p className="text-lg text-blue-100 mb-8 leading-relaxed">
              Your personal medical ID card — designed to give first responders
              instant access to your critical health information during any emergency.
            </p>
            <div className="flex items-center gap-4">
              <button
                onClick={() => scrollTo("features")}
                className="px-6 py-3 bg-white text-[#2d348f] font-bold rounded-lg shadow-lg hover:bg-blue-50 transition-all"
              >
                Learn More
              </button>
              <button
                onClick={() => scrollTo("contact")}
                className="px-6 py-3 border-2 border-white text-white font-bold rounded-lg hover:bg-white/10 transition-all"
              >
                Contact
              </button>
            </div>
          </div>

          <div className="hidden lg:flex flex-1 justify-end items-center">
            <img
              src={`${process.env.PUBLIC_URL}/SAL%20Card%204-25-B.png`}
              alt="SAL Mine Medical ID Card"
              className="w-80 rounded-2xl shadow-2xl border-4 border-white/20"
            />
          </div>
        </div>
      </section>

      {/* BANNER 2 */}
      <div className="w-full bg-[#445eed] py-5 px-6 text-center">
        <p className="text-white font-semibold text-base tracking-wide max-w-3xl mx-auto leading-relaxed">
          What is S.A.L?&nbsp;&nbsp;·&nbsp;&nbsp;S.A.L stands for <span className="font-extrabold">Save A Life.</span>&nbsp;&nbsp;
          Simply put, S.A.L is a bandage — however, it is no ordinary bandage.&nbsp;&nbsp;
          It can save your life.
        </p>
      </div>

      {/* KEY FEATURES */}
      <section id="features" className="py-20 bg-[#f7f8fa]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold text-[#2d348f] text-center mb-3">Key Features</h2>
          <p className="text-center text-[#837f90] mb-12 max-w-xl mx-auto">
            Built for real emergencies. Every feature is designed with your safety and first responders in mind.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { emoji: "🛡️", title: "Durable Design", desc: "Built to withstand the demands of daily life. Our cards are scratch-resistant and engineered to last for years without fading or cracking." },
              { emoji: "💧", title: "Waterproof", desc: "Whether you're caught in the rain or facing a water emergency, your SAL Mine card stays intact and fully readable in all conditions." },
              { emoji: "📱", title: "Digital Integration", desc: "Your physical card is synced to a secure digital profile. Update your medical information anytime from any device — changes reflect instantly." },
              { emoji: "🔒", title: "Secure Access", desc: "First responders access your information via QR scan with no login required on their end. Your data remains private and HIPAA-compliant at all times." },
              { emoji: "🌿", title: "Hypoallergenic", desc: "Made with hypoallergenic materials, our cards are safe for all skin types. Wear it as a wristband or carry it in your wallet without concern." },
              { emoji: "🏥", title: "EMR Integration", desc: "SAL Mine connects with Electronic Medical Record systems, allowing seamless data flow between your profile and healthcare providers." },
            ].map((f) => (
              <div key={f.title} className="bg-white rounded-2xl shadow-md border border-[#e5e7eb] p-6 flex flex-col gap-3 hover:shadow-lg transition-shadow">
                <span className="text-4xl">{f.emoji}</span>
                <h3 className="text-lg font-bold text-[#2d348f]">{f.title}</h3>
                <p className="text-sm text-[#837f90] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW SAL MINE WORKS */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold text-[#2d348f] text-center mb-3">How SAL Mine Works</h2>
          <p className="text-center text-[#837f90] mb-12">Three simple steps to a safer you.</p>

          <div className="bg-gradient-to-br from-[#f7f8fa] to-[#e6e9f5] rounded-2xl border border-[#e5e7eb] shadow-lg overflow-hidden">
            <div className="p-10 min-h-[220px] flex flex-col justify-center">
              <div className="text-6xl font-extrabold text-[#4962d8]/20 leading-none mb-2">
                {slides[currentSlide].step}
              </div>
              <h3 className="text-2xl font-extrabold text-[#2d348f] mb-3">
                {slides[currentSlide].title}
              </h3>
              <p className="text-[#837f90] leading-relaxed max-w-xl">
                {slides[currentSlide].description}
              </p>
            </div>
            <div className="flex items-center justify-between px-10 pb-8">
              <div className="flex gap-2">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentSlide(i)}
                    className={`h-3 rounded-full transition-all focus:outline-none ${i === currentSlide ? "bg-[#2d348f] w-6" : "bg-[#4962d8]/30 w-3"}`}
                  />
                ))}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setCurrentSlide((p) => (p === 0 ? slides.length - 1 : p - 1))}
                  className="w-10 h-10 rounded-full border-2 border-[#4962d8] text-[#4962d8] text-xl flex items-center justify-center hover:bg-[#4962d8] hover:text-white transition-all focus:outline-none"
                >
                  ‹
                </button>
                <button
                  onClick={() => setCurrentSlide((p) => (p === slides.length - 1 ? 0 : p + 1))}
                  className="w-10 h-10 rounded-full border-2 border-[#4962d8] text-[#4962d8] text-xl flex items-center justify-center hover:bg-[#4962d8] hover:text-white transition-all focus:outline-none"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEDICAL APPLICATIONS */}
      <section id="applications" className="py-20 bg-[#f7f8fa]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold text-[#2d348f] text-center mb-3">Medical Applications</h2>
          <p className="text-center text-[#837f90] mb-12 max-w-xl mx-auto">
            SAL Mine is built for anyone who needs their health information accessible at a moment's notice.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                emoji: "❤️",
                gradient: "from-red-50 to-red-100",
                border: "border-red-200",
                titleColor: "#dc2626",
                title: "Chronic Conditions",
                desc: "For patients managing diabetes, heart disease, epilepsy, or other ongoing conditions, SAL Mine ensures first responders know exactly what they're dealing with before treatment begins — preventing dangerous errors.",
              },
              {
                emoji: "💊",
                gradient: "from-blue-50 to-[#e6e9f5]",
                border: "border-blue-200",
                titleColor: "#2d348f",
                title: "Medication Alerts",
                desc: "Drug interactions can be fatal in emergencies. SAL Mine prominently displays your current medications and dosages so paramedics and ER staff can make informed, life-saving decisions instantly.",
              },
              {
                emoji: "🤝",
                gradient: "from-green-50 to-green-100",
                border: "border-green-200",
                titleColor: "#16a34a",
                title: "Special Needs",
                desc: "For individuals with autism, cognitive differences, or sensory sensitivities, SAL Mine communicates critical behavioral and care information to responders who may not otherwise know how to provide appropriate assistance.",
              },
            ].map((app) => (
              <div key={app.title} className={`bg-gradient-to-br ${app.gradient} rounded-2xl border ${app.border} shadow-md overflow-hidden flex flex-col`}>
                <div className="flex items-center justify-center py-12 text-7xl bg-white/40">
                  {app.emoji}
                </div>
                <div className="p-6 flex flex-col gap-2">
                  <h3 className="text-xl font-bold" style={{ color: app.titleColor }}>{app.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{app.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold text-[#2d348f] text-center mb-3">What People Are Saying</h2>
          <p className="text-center text-[#837f90] mb-12">Trusted by patients, caregivers, and medical professionals.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                initials: "MT",
                name: "Maria T.",
                role: "Patient — Type 1 Diabetic",
                quote: "I was in a car accident and couldn't speak. The paramedic scanned my SAL Mine card and knew about my insulin dependency within seconds. It genuinely saved my life.",
              },
              {
                initials: "JR",
                name: "James R.",
                role: "Emergency Medical Technician",
                quote: "As a first responder, those first two minutes are everything. SAL Mine gives me the patient's full medical picture instantly. Every patient should have one of these cards.",
              },
              {
                initials: "LK",
                name: "Linda K.",
                role: "Caregiver — Parent of a child with Autism",
                quote: "My son can't always communicate in stressful situations. SAL Mine tells responders exactly how to approach him safely. The peace of mind it gives me is priceless.",
              },
            ].map((t) => (
              <div key={t.name} className="bg-[#f7f8fa] rounded-2xl border border-[#e5e7eb] shadow-md p-8 flex flex-col gap-4">
                <div className="text-4xl text-[#4962d8] leading-none">"</div>
                <p className="text-gray-700 leading-relaxed text-sm italic flex-1">{t.quote}</p>
                <div className="flex items-center gap-3 pt-4 border-t border-[#e5e7eb]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#445eed] to-[#2d348f] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {t.initials}
                  </div>
                  <div>
                    <div className="font-bold text-[#2d348f] text-sm">{t.name}</div>
                    <div className="text-xs text-[#837f90]">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 bg-gradient-to-br from-[#f7f8fa] to-[#e6e9f5]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold text-[#2d348f] text-center mb-3">Pricing Plans</h2>
          <p className="text-center text-[#837f90] mb-12">Simple, transparent pricing. No hidden fees.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Basic */}
            <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-md p-8 flex flex-col gap-4">
              <div className="text-xs font-bold text-[#837f90] uppercase tracking-widest">Basic</div>
              <div className="flex items-end gap-1">
                <span className="text-5xl font-extrabold text-[#2d348f]">$5</span>
                <span className="text-[#837f90] mb-2">/month</span>
              </div>
              <p className="text-sm text-gray-600">
                Perfect for individuals who need a reliable medical ID card with their essential information on hand.
              </p>
              <ul className="flex flex-col gap-2 mt-2 flex-1">
                {[
                  "SAL Mine physical ID card",
                  "QR code linked to your profile",
                  "Basic medical profile (allergies, blood type, medications)",
                  "Emergency contact display",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-green-500 font-bold mt-0.5">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/signup"
                className="mt-4 block text-center py-3 rounded-lg border-2 border-[#2d348f] text-[#2d348f] font-bold hover:bg-[#2d348f] hover:text-white transition-all"
              >
                Get Started
              </Link>
            </div>

            {/* Premium */}
            <div className="bg-gradient-to-br from-[#445eed] to-[#2d348f] rounded-2xl shadow-xl p-8 flex flex-col gap-4 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full">
                Most Popular
              </div>
              <div className="text-xs font-bold text-blue-200 uppercase tracking-widest">Premium</div>
              <div className="flex items-end gap-1">
                <span className="text-5xl font-extrabold text-white">$10</span>
                <span className="text-blue-200 mb-2">/month</span>
              </div>
              <p className="text-sm text-blue-100">
                Full platform access for patients and caregivers who want complete digital health management.
              </p>
              <ul className="flex flex-col gap-2 mt-2 flex-1">
                {[
                  "Everything in Basic",
                  "Full digital health profile",
                  "Doctor & hospital directory integration",
                  "Document storage (labs, imaging, records)",
                  "EMR integration",
                  "Multi-language support",
                  "Priority customer support",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-white">
                    <span className="text-blue-200 font-bold mt-0.5">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/signup"
                className="mt-4 block text-center py-3 rounded-lg bg-white text-[#2d348f] font-bold hover:bg-blue-50 transition-all"
              >
                Get Full Access
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TECHNICAL SPECIFICATIONS */}
      <section id="specifications" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold text-[#2d348f] text-center mb-3">Technical Specifications</h2>
          <p className="text-center text-[#837f90] mb-12">
            Enterprise-grade infrastructure built for reliability and compliance.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                category: "Card",
                specs: [
                  ["Material", "PVC + Polycarbonate overlay"],
                  ["Dimensions", "3.375\" × 2.125\" (CR80 standard)"],
                  ["Thickness", "0.76mm"],
                  ["QR Code", "ISO/IEC 18004 compliant"],
                  ["Durability", "Rated 5+ years"],
                  ["Water Resistance", "IP68 rated"],
                ],
              },
              {
                category: "Platform",
                specs: [
                  ["Encryption", "AES-256 at rest, TLS 1.3 in transit"],
                  ["Compliance", "HIPAA, HITECH"],
                  ["Uptime SLA", "99.9%"],
                  ["Data Backup", "Daily encrypted backups"],
                  ["Authentication", "JWT + 2FA"],
                  ["Hosting", "HIPAA-compliant cloud infrastructure"],
                ],
              },
            ].map((block) => (
              <div key={block.category} className="bg-[#f7f8fa] rounded-2xl border border-[#e5e7eb] p-6">
                <h3 className="text-lg font-bold text-[#2d348f] mb-4">{block.category}</h3>
                <table className="w-full text-sm">
                  <tbody>
                    {block.specs.map(([label, value]) => (
                      <tr key={label} className="border-b border-[#e5e7eb] last:border-0">
                        <td className="py-2.5 text-[#837f90] font-medium w-1/2">{label}</td>
                        <td className="py-2.5 text-gray-800 font-semibold">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-20 bg-[#f7f8fa]">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold text-[#2d348f] text-center mb-3">Contact Us</h2>
          <p className="text-center text-[#837f90] mb-10">Have a question or need support? We're here to help.</p>
          <div className="bg-white rounded-2xl shadow-xl border border-[#e5e7eb] p-8 flex flex-col gap-5">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-[#837f90]">Full Name</label>
              <input
                name="name"
                value={contactForm.name}
                onChange={handleContactChange}
                placeholder="Jane Doe"
                className="border-0 border-b-2 border-[#4962d8] focus:outline-none text-sm py-1 bg-transparent text-black font-medium"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-[#837f90]">Email Address</label>
              <input
                name="email"
                type="email"
                value={contactForm.email}
                onChange={handleContactChange}
                placeholder="jane@example.com"
                className="border-0 border-b-2 border-[#4962d8] focus:outline-none text-sm py-1 bg-transparent text-black font-medium"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-[#837f90]">Message</label>
              <textarea
                name="message"
                value={contactForm.message}
                onChange={handleContactChange}
                rows={4}
                placeholder="How can we help you?"
                className="border-0 border-b-2 border-[#4962d8] focus:outline-none text-sm py-1 bg-transparent text-black font-medium resize-none"
              />
            </div>
            <button
              type="button"
              onClick={() => setContactForm({ name: "", email: "", message: "" })}
              className="w-full py-3 font-bold text-white text-base bg-gradient-to-r from-[#445eed] to-[#2d348f] hover:from-[#3916ab] hover:to-[#4962d8] transition-colors rounded-lg shadow-md mt-2"
            >
              Send Message
            </button>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="py-16 bg-gradient-to-r from-[#2d348f] to-[#445eed]">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-extrabold text-white mb-2">Stay Updated</h2>
          <p className="text-blue-200 mb-8">
            Get the latest updates on SAL Mine features, health tips, and emergency preparedness.
          </p>
          <div className="flex gap-3 max-w-md mx-auto">
            <input
              type="email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white placeholder-blue-200 focus:outline-none focus:border-white text-sm"
            />
            <button
              onClick={() => setNewsletterEmail("")}
              className="px-5 py-3 bg-white text-[#2d348f] font-bold rounded-lg hover:bg-blue-50 transition-all text-sm"
            >
              Subscribe
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#f7f8fa]">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold text-[#2d348f] text-center mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-center text-[#837f90] mb-12">
            Everything you need to know about S.A.L Mine.
          </p>
          <div className="flex flex-col gap-3">
            {[
              {
                q: "How do I enter my medical information on the Medical Data Card?",
                a: "As a member of the S.A.L website, both you or your medical provider can enter your medical information online and print out the Medical Data Card to insert into your S.A.L Mine Bandage.",
              },
              {
                q: "Where do I place the Medical Data Bandage on my body?",
                a: "You can place it anywhere so that it is readily visible. However, we recommend putting this card directly over your heart or on the left side of your abdomen — as paramedics will be looking in these places.",
              },
              {
                q: "What if my medical information changes?",
                a: "Either you or your healthcare provider can enter or update your information and print out the new card form from our website.",
              },
              {
                q: "What about rain or water?",
                a: "S.A.L is a water-resistant bandage — it will be safe from rain or water.",
              },
              {
                q: "How often do I change the bandage?",
                a: "It depends on your activities of daily living. You may need to replace it every few days, or in most cases it will last longer than a week.",
              },
              {
                q: "How will the medical professional know I have S.A.L if it is underneath my clothing?",
                a: "Most paramedics and medical professionals are trained to look for S.A.L because they are well informed — or you may wish to wear one of our charms, necklaces, or even bracelets. Other S.A.L Medical alerts are available on our website.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left focus:outline-none"
                >
                  <span className="font-bold text-[#2d348f] text-sm pr-4">{item.q}</span>
                  <span className={`text-[#4962d8] text-xl font-bold flex-shrink-0 transition-transform duration-200 ${openFaq === i ? "rotate-45" : ""}`}>
                    +
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-sm text-[#837f90] leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#1a1f5e] text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <img src={`${process.env.PUBLIC_URL}/SAL-logoR3.jpg`} alt="SAL Mine" className="h-8 w-8 object-contain rounded" />
                <span className="font-extrabold text-lg">SAL Mine</span>
              </div>
              <p className="text-blue-200 text-sm leading-relaxed">
                Your personal medical ID — giving first responders instant access to the information that saves lives.
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-3 text-blue-200 text-xs uppercase tracking-widest">Product</h4>
              <ul className="flex flex-col gap-2 text-sm text-blue-100">
                {[
                  { label: "Features", target: "features" },
                  { label: "Pricing", target: "pricing" },
                  { label: "Technical Specs", target: "specifications" },
                  { label: "Security", target: "specifications" },
                ].map((item) => (
                  <li key={item.label}>
                    <button onClick={() => scrollTo(item.target)} className="hover:text-white transition-colors focus:outline-none">
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-3 text-blue-200 text-xs uppercase tracking-widest">Company</h4>
              <ul className="flex flex-col gap-2 text-sm text-blue-100">
                <li>
                  <Link to="/about" className="hover:text-white transition-colors">About Us</Link>
                </li>
                <li>
                  <button onClick={() => scrollTo("contact")} className="hover:text-white transition-colors focus:outline-none">Contact</button>
                </li>
                <li>
                  <button className="hover:text-white transition-colors focus:outline-none">Careers</button>
                </li>
                <li>
                  <button className="hover:text-white transition-colors focus:outline-none">Press</button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-3 text-blue-200 text-xs uppercase tracking-widest">Legal</h4>
              <ul className="flex flex-col gap-2 text-sm text-blue-100">
                <li>
                  <Link to="/terms-and-conditions" className="hover:text-white transition-colors">
                    Terms of Use
                  </Link>
                </li>
                <li>
                  <button className="hover:text-white transition-colors focus:outline-none">Privacy Policy</button>
                </li>
                <li>
                  <button className="hover:text-white transition-colors focus:outline-none">HIPAA Notice</button>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-blue-300">
            <span>© {new Date().getFullYear()} Save A Life Mine. All rights reserved.</span>
            <span>HIPAA Compliant · AES-256 Encrypted · Built in the USA</span>
          </div>
        </div>
      </footer>

    </div>
  );
}
